import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Paper } from '@mui/material';
import { CloudUpload as UploadIcon, CheckCircle as SuccessIcon, Error as ErrorIcon } from '@mui/icons-material';
import { PhotoUploadProgress } from '../types';

interface PhotoUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  onBeforeOpen?: () => void;
  uploadProgress?: PhotoUploadProgress[];
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedFileTypes?: string[];
}

const PhotoUploadZone: React.FC<PhotoUploadZoneProps> = ({
  onFilesSelected,
  onBeforeOpen,
  uploadProgress = [],
  maxFiles = 300,
  maxFileSize = 30 * 1024 * 1024, // 30MB
  acceptedFileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'],
}) => {
  const [dragActive, setDragActive] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: any[]) => {
      setDragActive(false);

      if (rejectedFiles.length > 0) {
        console.warn('Rejected files:', rejectedFiles);
      }

      if (acceptedFiles.length > 0) {
        onFilesSelected(acceptedFiles);
      }
    },
    [onFilesSelected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/heic': ['.heic', '.HEIC'],
    },
    maxFiles,
    maxSize: maxFileSize,
    multiple: true,
    noDragEventsBubbling: true,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
  });

  const getUploadStatusIcon = (status: PhotoUploadProgress['status']) => {
    switch (status) {
      case 'success':
        return <SuccessIcon color='success' />;
      case 'error':
        return <ErrorIcon color='error' />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Upload Zone */}
      <Paper
        {...getRootProps({ onClick: () => onBeforeOpen?.() })}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive || dragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive || dragActive ? 'primary.50' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.50',
          },
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant='h6' gutterBottom>
          {isDragActive || dragActive ? 'Drop photos here...' : 'Drag & drop photos here, or click to select'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Supports JPG, PNG and HEIC files
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Maximum {maxFiles} photos, 30MB per file
        </Typography>
      </Paper>
    </Box>
  );
};

export default PhotoUploadZone;
